import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useReturnTo } from '../common/useReturnTo';
import { useTranslation, Trans } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronDown, faCheck } from "@fortawesome/free-solid-svg-icons";

import RouteHeader from "../common/RouteHeader";
import "./PanelHeader.css";

/**
 * Fused header for the private-classes Panel. Mirrors {@link PanelHeader} but
 * scoped by teacher instead of class:
 *  · 0 teachers → static "no private teachers" line.
 *  · 1 teacher  → static "Prof. Name".
 *  · N > 1      → dropdown selector.
 *
 * Search/archived variants from PanelHeader are omitted here — the private-classes
 * teacher list is small by nature. Add them when a school actually needs them.
 */
const PanelPrivateHeader = ({ schoolName, currentTeacher, availableTeachers, onTeacherChange, failed = false }) => {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);
	const wrapRef = useRef(null);

	const teachers = availableTeachers || [];
	const isMulti = teachers.length > 1;

	useEffect(() => {
		if (!open) return;
		const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
		document.addEventListener('mousedown', onDown);
		return () => document.removeEventListener('mousedown', onDown);
	}, [open]);

	// Enquanto `availableTeachers` é undefined (snapshot ainda não voltou),
	// mostra shimmer no lugar do título — alinha com o skeleton do body (Fatia
	// C do loading system) e fecha o débito antigo (era string vazia).
	// Placeholder só em REAL empty state.
	const loadingSnapshot = availableTeachers === undefined;
	const displayName = formatTeacherName(currentTeacher?.name, t);
	/* 🔴 `failed` distingue "lista vazia porque ninguém tem aula particular"
	   de "lista vazia porque a carga falhou" (2026-08-28). Sem isso o erro
	   herdava o placeholder "Selecione um professor" — convite a um gesto que a
	   tela não oferece, e que descreve o estado do componente, não a situação.
	   Com falha o título é o NOME DA TELA; o que houve, quem diz é o corpo. */
	const title = failed
		? t("header.navigation-titles.panel-private", "Aulas Particulares")
		: displayName || (loadingSnapshot ? '' : t("panel.private.pick-teacher"));

	const handlePick = (id) => {
		if (id !== currentTeacher?.teacherId) onTeacherChange(id);
		setOpen(false);
	};

	// School crumb first — só multi-scope (parent decide via prop). Painéis são
	// school-scoped; sem esse crumb, multi-scope users perdem o "em qual escola
	// estou" (setPageHeader sobrescreve o RouteHeader default do MainPage).
	/* ⚠️ `null` no fallback: sem origem NÃO há voltar. Quem chegou pela sidebar
	   está na raiz da seção, e seta que volta para lugar nenhum é pior que seta
	   nenhuma. Ver `useReturnTo`. */
	const voltarPara = useReturnTo(null);

	const crumbs = [
		/* 🔴 ESCOLA ou PAINEL, nunca os dois (PO, 2026-08-28). Os dois ocupam o
		   MESMO slot de contexto no L1, e cada um serve a um usuário:
		     · multi-escola / root → o nome da escola responde "em qual estou"
		     · escola única (Rômulo, Wallace) → o nome é ruído, e "Painel"
		       devolve o contexto do módulo que o crumb de módulo não dá
		   `schoolName` já é null exatamente quando há uma escola só — a
		   condição não precisou ser inventada, só lida.
		   ⚠️ No mobile o "Painel" some junto (`pnl-crumb-hide-mobile`): lá o
		   espaço é o recurso escasso e o módulo à direita já situa. */
		...(schoolName
			? [{ label: schoolName },
			   { label: <Trans i18nKey="header.navigation-titles.panel">Painel</Trans>, className: 'pnl-crumb-hide-mobile' }]
			: [{ label: <Trans i18nKey="header.navigation-titles.panel">Painel</Trans> }]),
		{ label: <Trans i18nKey="panel.private.crumb">Aulas Particulares</Trans> },
	];

	const titleNode = loadingSnapshot ? (
		<span className="sk" style={{ width: 180, height: 22, borderRadius: 4, display: 'inline-block' }} aria-hidden="true" />
	) : isMulti ? (
		<button
			type="button"
			className={"pnl-header-h1sel" + (open ? " pnl-header-h1sel-open" : "")}
			onClick={() => setOpen(!open)}
		>
			<span className="pnl-header-h1sel-txt">{title}</span>
			<FontAwesomeIcon icon={faChevronDown} className="pnl-header-cv" />
		</button>
	) : (
		<div className="pnl-header-h1static">{title}</div>
	);

	return (
		<div className="pnl-header">
			{/* 🔴 MESMA peça do `PanelStudentHeader`: `back-btn` IRMÃO do titlewrap,
			    dentro do `pnl-header`. Cheguei a inventar uma seta e um CSS novos
			    dentro do `RouteHeader`; o portal já tinha a sua. */}
			{voltarPara && (
				<Link to={voltarPara} className="back-btn" aria-label={t('panel.student.back')}>
					<FontAwesomeIcon icon={faChevronLeft} />
				</Link>
			)}
			<div className="pnl-header-titlewrap" ref={wrapRef}>
				<RouteHeader crumbs={crumbs} title={titleNode} />

				{open && isMulti && (
					<div className="pnl-header-dd">
						{teachers.map(tc => (
							<div
								key={tc.teacherId}
								className={"pnl-header-ddi" + (tc.teacherId === currentTeacher?.teacherId ? " pnl-header-ddi-on" : "")}
								onClick={() => handlePick(tc.teacherId)}
							>
								{formatTeacherName(tc.name, t)}
								{tc.teacherId === currentTeacher?.teacherId && (
									<FontAwesomeIcon icon={faCheck} className="pnl-header-ddi-ck" />
								)}
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

/** "Prof. Firstname Lastname" — backend hands over raw names, prefix is UI-side. */
function formatTeacherName(name, t) {
	if (!name || !name.trim()) return null;
	return t('panel.private.teacher-name', { name: name.trim() });
}

export default PanelPrivateHeader;
